<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { gsap } from "gsap";
import { useGlobalStore } from "@stores/global";
import { useGlobeStore } from "@stores/globe";
import { useDataStore } from "@stores/data";

import ClipsList from "@components/explore/ClipsList.vue";

const globalStore = useGlobalStore();
const globeStore = useGlobeStore();
const dataStore = useDataStore();

const clips = computed(() => {
    const name = globeStore.selectedCountry;
    if (!name) return [];
    let city = dataStore.pinnedCities.find((c) => c.name === name);
    if (!city) {
        const firstCityName = globeStore.firstCityForProvince[name];
        if (firstCityName)
            city = dataStore.pinnedCities.find((c) => c.name === firstCityName);
    }
    return city?.clips ?? [];
});

const rootEl = ref(null);

function playReveal() {
    if (!rootEl.value) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const targets = rootEl.value.querySelectorAll("[data-reveal]");
    if (!targets.length) return;

    gsap.killTweensOf(targets);
    gsap.fromTo(
        targets,
        { opacity: 0, y: 16 },
        {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: "power2.out",
            stagger: 0.06,
        },
    );
}

watch(
    () => globalStore.panelActive("clips"),
    async (active) => {
        if (!active) return;
        await nextTick();
        playReveal();
    },
    { immediate: true },
);

watch(
    () => globeStore.selectedCountry,
    async () => {
        if (!globalStore.panelActive("clips")) return;
        await nextTick();
        playReveal();
    },
);
</script>

<template>
    <Transition name="slide-left" appear>
        <div
            v-if="globalStore.panelActive('clips') && clips.length"
            ref="rootEl"
            class="clips bg-marine-800 border-marine-500 overflow-y-auto border-r p-6"
        >
            <div data-reveal class="mb-6 flex flex-col gap-2">
                <p class="h6 leading-none font-bold uppercase">Speed's Clips</p>
            </div>
            <ClipsList :clips="clips" :city-name="globeStore.selectedCountry" />
        </div>
    </Transition>
</template>

<style scoped>
.clips {
    grid-column: 1 / 7;
    grid-row: 2 / -1;
    z-index: 2;
}

@media (max-width: 1024px) {
    .clips {
        display: none;
    }
}
</style>
